import React, { useContext, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation, Link as RouterLink } from "react-router-dom";
import { ThemeProvider } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import Drawer from "@material-ui/core/Drawer";
import IconButton from "@material-ui/core/IconButton";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import Tooltip from "@material-ui/core/Tooltip";
import Badge from "@material-ui/core/Badge";

import MenuIcon from "@material-ui/icons/Menu";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import DashboardOutlinedIcon from "@material-ui/icons/DashboardOutlined";
import FlashOnIcon from "@material-ui/icons/FlashOn";
import ContactPhoneOutlinedIcon from "@material-ui/icons/ContactPhoneOutlined";
import SettingsOutlinedIcon from "@material-ui/icons/SettingsOutlined";
import SecurityIcon from "@material-ui/icons/Security";
import Brightness4Icon from "@material-ui/icons/Brightness4";
import Brightness7Icon from "@material-ui/icons/Brightness7";
import MoreHorizIcon from "@material-ui/icons/MoreHoriz";
import ChatBubbleOutlineIcon from "@material-ui/icons/ChatBubbleOutline";
import { Campaign as CampaignIcon, Psychology as PsychologyIcon } from "@mui/icons-material";

import { AuthContext } from "../context/Auth/AuthContext";
import { LayoutPreferenceContext } from "../context/LayoutPreference/LayoutPreferenceContext";
import ColorModeContext from "../layout/themeContext";
import useTickets from "../hooks/useTickets";
import usePlans from "../hooks/usePlans";
import NotificationsPopOver from "../components/NotificationsPopOver";
import MyTasksPopover from "../components/MyTasksPopover";
import UserModal from "../components/UserModal";
import UserLanguageSelector from "../components/UserLanguageSelector";
import TutorialAssistantBubble from "../components/TutorialAssistantBubble";
import { i18n } from "../translate/i18n";

import { createNewLayoutTheme, loadInterFont } from "./theme";
import {
  GERENCIA_TABS,
  CONTATOS_TABS,
  CONFIG_TABS,
  PRODUTIVIDADE_TABS,
  CAMPANHAS_TABS,
  IA_TABS,
  isGerenciaVisible,
  isConfiguracoesVisible,
  getGroupForPath,
} from "./menuConfig";

const SIDEBAR_WIDTH = 240;
const SIDEBAR_WIDTH_COLLAPSED = 72;
const MOBILE_BREAKPOINT = "(max-width:900px)";
const COLLAPSE_STORAGE_KEY = "voicechat.layoutNew.sidebarCollapsed";

const NAV_ITEMS = [
  { key: "atendimentos", to: "/tickets", label: "Atendimentos", icon: <ChatBubbleOutlineIcon /> },
  { key: "gerencia", to: "/", label: i18n.t("mainDrawer.listItems.management"), icon: <DashboardOutlinedIcon /> },
  { key: "produtividade", to: "/commercial", label: "Produtividade", icon: <FlashOnIcon /> },
  { key: "contatos", to: "/contacts", label: "Contatos & CRM", icon: <ContactPhoneOutlinedIcon /> },
  { key: "campanhas", to: "/campaigns", label: i18n.t("mainDrawer.listItems.campaigns"), icon: <CampaignIcon /> },
  { key: "ia", to: "/knowledge-base", label: "IA & Conhecimento", icon: <PsychologyIcon /> },
];

const ADMIN_ITEMS = [
  // "Administracao" (Novo) ainda nao tem uma tela propria no Classico -
  // o mais proximo e' /companies, so visivel pra user.super. Ver nota de
  // entrega: pode precisar de definicao melhor.
  { key: "administracao", to: "/companies", label: "Administração", icon: <SecurityIcon />, superOnly: true },
  { key: "configuracoes", to: "/users", label: "Configurações", icon: <SettingsOutlinedIcon /> },
];

const initials = (name) => {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] || "";
  const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
  return (first + last).toUpperCase();
};

const NewSidebar = ({ collapsed, setCollapsed, mobileOpen, setMobileOpen, isMobile, tokens, user, ticketBadge }) => {
  const location = useLocation();
  const currentGroup = getGroupForPath(location.pathname);

  const items = [
    ...NAV_ITEMS,
    ...ADMIN_ITEMS.filter((item) => !item.superOnly || user.super),
  ];

  const width = collapsed && !isMobile ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH;

  const content = (
    <div
      style={{
        width,
        height: "100%",
        boxSizing: "border-box",
        background: tokens.surface,
        borderRight: `1px solid ${tokens.border}`,
        padding: "20px 14px",
        display: "flex",
        flexDirection: "column",
        gap: 24,
        transition: "width .15s ease",
        overflow: "hidden",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 6px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
          <div
            style={{
              width: 36,
              height: 36,
              flexShrink: 0,
              borderRadius: 10,
              background: tokens.primary,
              color: "#FFFFFF",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 700,
              fontSize: 18,
            }}
          >
            V
          </div>
          {(!collapsed || isMobile) && (
            <span style={{ fontWeight: 700, fontSize: 19, color: tokens.primaryDark, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              voicechat
            </span>
          )}
        </div>
        {!isMobile && (
          <Tooltip title={collapsed ? "Expandir menu" : "Recolher menu"} placement="right">
            <IconButton aria-label="Recolher menu" size="small" onClick={() => setCollapsed((v) => !v)}>
              <ChevronLeftIcon style={{ transform: collapsed ? "rotate(180deg)" : "none", transition: "transform .15s" }} />
            </IconButton>
          </Tooltip>
        )}
      </div>

      <nav aria-label="Menu principal" style={{ display: "flex", flexDirection: "column", gap: 2, overflowY: "auto" }}>
        {items.map((item, idx) => {
          const isActive = currentGroup?.key === item.key || (item.key === "gerencia" && location.pathname === "/");
          const isDivider = item.key === "administracao";
          const label = collapsed && !isMobile ? "" : item.label;
          return (
            <React.Fragment key={item.key}>
              {isDivider && <div style={{ height: 1, background: tokens.divider, margin: "10px 12px" }} />}
              <Tooltip title={collapsed && !isMobile ? item.label : ""} placement="right">
                <RouterLink
                  to={item.to}
                  onClick={() => isMobile && setMobileOpen(false)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    height: 44,
                    padding: "0 12px",
                    borderRadius: 10,
                    textDecoration: "none",
                    background: isActive ? tokens.primarySoft : "transparent",
                    color: isActive ? tokens.primaryText : tokens.text2,
                    fontWeight: isActive ? 600 : 500,
                    fontSize: 14,
                  }}
                >
                  {item.icon}
                  {label && <span style={{ flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{label}</span>}
                  {label && item.key === "atendimentos" && ticketBadge > 0 && (
                    <span
                      style={{
                        minWidth: 22,
                        height: 22,
                        padding: "0 6px",
                        boxSizing: "border-box",
                        borderRadius: 11,
                        background: tokens.primary,
                        color: "#FFFFFF",
                        fontSize: 12,
                        fontWeight: 600,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {ticketBadge}
                    </span>
                  )}
                </RouterLink>
              </Tooltip>
            </React.Fragment>
          );
        })}
      </nav>

      {(!collapsed || isMobile) && (
        <div style={{ marginTop: "auto", padding: "0 12px", fontSize: 12, color: tokens.textMuted2 }}>
          Versão 3.9.0
        </div>
      )}
    </div>
  );

  if (isMobile) {
    return (
      <Drawer anchor="left" open={mobileOpen} onClose={() => setMobileOpen(false)}>
        {content}
      </Drawer>
    );
  }

  return content;
};

const NewTabs = ({ tabs, user, flags, tokens }) => {
  const location = useLocation();
  const visible = tabs.filter((t) => t.isVisible(user, flags));
  if (visible.length === 0) return null;

  return (
    <div
      role="tablist"
      style={{
        display: "flex",
        gap: 24,
        padding: "0 24px",
        background: tokens.surface,
        borderBottom: `1px solid ${tokens.border}`,
        overflowX: "auto",
      }}
    >
      {visible.map((tab) => {
        const isActive = tab.to === "/" ? location.pathname === "/" : location.pathname.startsWith(tab.to);
        return (
          <RouterLink
            key={tab.to}
            to={tab.to}
            role="tab"
            aria-selected={isActive}
            style={{
              padding: "10px 0",
              marginTop: 0,
              textDecoration: "none",
              fontSize: 14,
              fontWeight: isActive ? 600 : 500,
              color: isActive ? tokens.primaryDark : tokens.textMuted,
              borderBottom: isActive ? `2px solid ${tokens.primary}` : "2px solid transparent",
              whiteSpace: "nowrap",
            }}
          >
            {tab.label}
          </RouterLink>
        );
      })}
    </div>
  );
};

const NewHeader = ({ tokens, user, onToggleSidebar, isMobile, mode, toggleColorMode }) => {
  const [moreAnchor, setMoreAnchor] = useState(null);
  const [avatarAnchor, setAvatarAnchor] = useState(null);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const { layout, setLayout } = useContext(LayoutPreferenceContext);
  const { handleLogout } = useContext(AuthContext);
  const firstName = (user?.name || "").split(" ")[0];

  return (
    <header
      style={{
        minHeight: 64,
        boxSizing: "border-box",
        padding: "10px 24px",
        background: tokens.surface,
        borderBottom: `1px solid ${tokens.border}`,
        display: "flex",
        flexWrap: "wrap",
        gap: 12,
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
        {isMobile && (
          <IconButton aria-label="Abrir menu" onClick={onToggleSidebar}>
            <MenuIcon />
          </IconButton>
        )}
        <span style={{ fontSize: 16, fontWeight: 600, color: tokens.text }}>Olá, {firstName}</span>
        <span
          style={{
            height: 36,
            padding: "0 12px",
            border: `1px solid ${tokens.borderInput}`,
            background: tokens.surface,
            borderRadius: 10,
            display: "flex",
            alignItems: "center",
            fontSize: 14,
            color: tokens.text2,
          }}
        >
          {user?.company?.name || "Empresa"}
        </span>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {/* "Online" e' so' um indicador visual por enquanto - nao existe
            hoje um endpoint de status do atendente (presente/ausente) pra
            esse botao realmente mudar algo. Sinalizado na entrega. */}
        <span
          style={{
            height: 36,
            padding: "0 12px",
            border: "1px solid #CDEBD8",
            background: "#EEF9F2",
            borderRadius: 18,
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: 13,
            fontWeight: 500,
            color: "#1B6B3A",
          }}
        >
          <span style={{ width: 8, height: 8, borderRadius: 4, background: "#22A55B" }} />
          Online
        </span>
        <Tooltip title="Alternar tema">
          <IconButton aria-label="Alternar tema" onClick={toggleColorMode} style={{ color: tokens.neutralChipText }}>
            {mode === "dark" ? <Brightness7Icon fontSize="small" /> : <Brightness4Icon fontSize="small" />}
          </IconButton>
        </Tooltip>
        <MyTasksPopover />
        {user.id && <NotificationsPopOver volume={1} />}
        <Tooltip title="Mais opções">
          <IconButton aria-label="Mais opções" onClick={(e) => setMoreAnchor(e.currentTarget)} style={{ color: tokens.neutralChipText }}>
            <MoreHorizIcon />
          </IconButton>
        </Tooltip>
        <Menu anchorEl={moreAnchor} open={!!moreAnchor} onClose={() => setMoreAnchor(null)}>
          <MenuItem disabled style={{ opacity: 1, pointerEvents: "none" }}>
            <UserLanguageSelector />
          </MenuItem>
        </Menu>
        <IconButton
          aria-label={`Perfil de ${user?.name || ""}`}
          onClick={(e) => setAvatarAnchor(e.currentTarget)}
          style={{
            marginLeft: 4,
            width: 38,
            height: 38,
            borderRadius: 19,
            background: tokens.primaryDark,
            color: "#FFFFFF",
            fontSize: 14,
            fontWeight: 600,
          }}
        >
          {initials(user?.name)}
        </IconButton>
        <Menu anchorEl={avatarAnchor} open={!!avatarAnchor} onClose={() => setAvatarAnchor(null)}>
          <MenuItem onClick={() => { setUserModalOpen(true); setAvatarAnchor(null); }}>
            {i18n.t("mainDrawer.appBar.user.profile")}
          </MenuItem>
          <MenuItem
            onClick={() => {
              setLayout(layout === "new" ? "classic" : "new");
              setAvatarAnchor(null);
            }}
          >
            Layout: {layout === "new" ? "Novo ✓ (voltar ao Clássico)" : "Clássico (experimentar o Novo)"}
          </MenuItem>
          <MenuItem onClick={() => { setAvatarAnchor(null); handleLogout(); }}>
            {i18n.t("mainDrawer.appBar.user.logout")}
          </MenuItem>
        </Menu>
        <UserModal open={userModalOpen} onClose={() => setUserModalOpen(false)} userId={user?.id} />
      </div>
    </header>
  );
};

const NewLayout = ({ children }) => {
  const { user } = useContext(AuthContext);
  const { colorMode } = useContext(ColorModeContext);
  const location = useLocation();
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);

  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(COLLAPSE_STORAGE_KEY) === "true";
    } catch (e) {
      return false;
    }
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  const [flags, setFlags] = useState({});

  const { getPlanCompany } = usePlans();
  useEffect(() => {
    if (!user?.companyId) return;
    getPlanCompany(undefined, user.companyId).then((planConfigs) => {
      setFlags({
        showCampaigns: planConfigs.plan.useCampaigns,
        showKanban: planConfigs.plan.useKanban,
        showOpenAi: planConfigs.plan.useOpenAi,
        showIntegrations: planConfigs.plan.useIntegrations,
        showSchedules: planConfigs.plan.useSchedules,
        showInternalChat: planConfigs.plan.useInternalChat,
        showExternalApi: planConfigs.plan.useExternalApi,
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.companyId]);

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSE_STORAGE_KEY, String(collapsed));
    } catch (e) {
      // localStorage indisponivel - segue so' com o estado em memoria
    }
  }, [collapsed]);

  useEffect(() => {
    loadInterFont();
  }, []);

  const theme = useMemo(() => createNewLayoutTheme(colorMode?.mode || "light"), [colorMode?.mode]);
  const tokens = theme.newLayoutTokens;

  const { tickets: unreadTickets } = useTickets({ withUnreadMessages: "true" });

  const group = getGroupForPath(location.pathname);
  const tabsForGroup =
    group?.key === "gerencia" && isGerenciaVisible(user)
      ? GERENCIA_TABS
      : group?.key === "contatos"
      ? CONTATOS_TABS
      : group?.key === "configuracoes" && isConfiguracoesVisible(user)
      ? CONFIG_TABS
      : group?.key === "produtividade"
      ? PRODUTIVIDADE_TABS
      : group?.key === "campanhas"
      ? CAMPANHAS_TABS
      : group?.key === "ia"
      ? IA_TABS
      : null;

  if (!user?.id) return children;

  return (
    <ThemeProvider theme={theme}>
      {/* Fix 2026-10-22: telas que imprimem (Sobreaviso, relatorios, ...)
          so' escondiam a moldura do Classico no @media print
          (layout/index.js, classes appBar/drawerPaper/appBarSpacer) -
          o shell Novo e' uma arvore de componentes totalmente diferente
          (NewSidebar/NewHeader/NewTabs), entao nada escondia essa moldura
          na hora de imprimir por aqui, e a impressao saia com menu/
          cabecalho/abas junto do relatorio. Mesma ideia do Classico, so'
          via atributo em vez de classe JSS (esses elementos usam style
          inline, nao makeStyles). */}
      <style>{`
        @media print {
          [data-np-hide] { display: none !important; }
          [data-np-expand] { width: 100% !important; height: auto !important; overflow: visible !important; padding: 0 !important; margin: 0 !important; }
        }
      `}</style>
      <div style={{ width: "100%", height: "100vh", display: "flex", background: tokens.bg, fontFamily: "Inter, system-ui, sans-serif" }}>
        <div data-np-hide>
          <NewSidebar
            collapsed={collapsed}
            setCollapsed={setCollapsed}
            mobileOpen={mobileOpen}
            setMobileOpen={setMobileOpen}
            isMobile={isMobile}
            tokens={tokens}
            user={user}
            ticketBadge={unreadTickets?.length || 0}
          />
        </div>
        <div data-np-expand style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
          <div data-np-hide>
            <NewHeader
              tokens={tokens}
              user={user}
              onToggleSidebar={() => setMobileOpen(true)}
              isMobile={isMobile}
              mode={colorMode?.mode}
              toggleColorMode={colorMode?.toggleColorMode}
            />
          </div>
          {tabsForGroup && <div data-np-hide><NewTabs tabs={tabsForGroup} user={user} flags={flags} tokens={tokens} /></div>}
          {/* Achado 2026-10-22: com o Container das telas reaproveitadas
              esticando pra largura total (ver theme.js), uma rolagem
              horizontal fantasma de poucos pixels passou a aparecer
              (a barra vertical "rouba" espaco do calculo de largura).
              Nenhuma tela do layout Novo precisa de rolagem horizontal
              na pagina inteira - tabelas largas ja tem seu proprio
              overflow-x isolado (Contatos, etc.) - entao trava o eixo X
              aqui, so' o Y rola. */}
          {/* Fix 2026-10-22: usuario reportou (Comercial, Sobreaviso,
              Relatorios, Configuracoes) que a barra de rolagem vertical
              parava na metade da tela, com espaco em branco embaixo.
              Causa real: MainContainer (Classico, reaproveitado em
              varias telas) ja tem "flex:1" no seu proprio Container,
              esperando que o PAI (este <main>) seja um flex container -
              sem isso o "flex:1" do Container nao tem efeito nenhum e
              ele encolhe pro tamanho do CONTEUDO em vez de esticar pra
              preencher a tela, sobrando area vazia embaixo (e a barra
              de rolagem de cada tela, que vive dentro desse Container,
              fica curta/"na metade"). display:flex + flexDirection:
              column aqui deixa o "flex:1" ja existente no Container
              funcionar - tentativa anterior (so' height:"100%") nao
              bastou porque a cadeia de resolucao de % nao fechava. */}
          {/* Fix 2026-10-22: usuario reportou que mesmo com a cor certa,
              o conteudo parecia "entrar dentro de um envelope" em telas
              curtas - o paddingBottom:96 deixava uma faixa vazia grande
              sempre visivel, mesmo quando a tela nao tinha conteudo
              suficiente pra chegar perto do balao flutuante de IA
              (o motivo original do padding grande). O balao e'
              position:fixed (flutua por cima do conteudo, nao faz parte
              do fluxo) e e' arrastavel pelo proprio usuario - reduzido
              pra bater com o padding do topo (20px + uma folga de 12px),
              troca uma protecao total contra sobrepor o ultimo item de
              uma lista bem longa (caso raro, e o balao pode ser
              arrastado) por nao sobrar espaco morto enorme no caso
              comum (tela curta). */}
          <main data-np-expand style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflowY: "auto", overflowX: "hidden", padding: 20, paddingBottom: 32, background: tokens.bg }}>
            {children}
          </main>
        </div>
        <TutorialAssistantBubble />
      </div>
    </ThemeProvider>
  );
};

export default NewLayout;
